<template>
  <div class="content-wrapper ataviti-home">

    <!-- LOADING -->
    <div
      v-if="loading"
      class="content-body"
    >
      <div class="chantier-state">
        <b-spinner class="mb-1" />
        <p class="mt-1 mb-0">
          Chargement de la note…
        </p>
      </div>
    </div>

    <!-- ERROR -->
    <div
      v-else-if="error"
      class="content-body"
    >
      <div class="chantier-state chantier-state--error">
        <feather-icon
          icon="AlertTriangleIcon"
          size="32"
        />
        <p class="mt-1 mb-1">
          {{ error }}
        </p>
        <b-button
          variant="outline-primary"
          size="sm"
          @click="loadNote"
        >
          <feather-icon
            icon="RefreshCwIcon"
            size="14"
            class="mr-50"
          />
          Réessayer
        </b-button>
      </div>
    </div>

    <div
      v-else-if="note"
      class="content-body"
    >

      <router-link
        :to="{ name: 'chantier-plan-detail', params: { chantierId, planId } }"
        class="chantier-back"
      >
        <feather-icon
          icon="ArrowLeftIcon"
          size="14"
          class="mr-50"
        />
        Retour au plan d'action
      </router-link>

      <!-- HERO -->
      <header class="ataviti-home__banner">
        <div class="ataviti-home__banner-text">
          <h1 class="ataviti-home__title">
            {{ note.titre }}
          </h1>
          <div class="note-detail__meta">
            <span class="note-detail__date">
              <feather-icon
                icon="CalendarIcon"
                size="13"
                class="mr-50"
              />
              {{ formatLongDate(note.date) }}
            </span>
            <span
              v-if="isChantierPublic && note.compteRenduPublic"
              class="badge note-detail__public-badge"
            >
              <feather-icon
                icon="GlobeIcon"
                size="11"
                class="mr-25"
              />
              Compte-rendu public disponible
            </span>
            <span
              v-else
              class="badge note-detail__internal-badge"
            >
              <feather-icon
                icon="LockIcon"
                size="11"
                class="mr-25"
              />
              Interne uniquement
            </span>
          </div>
        </div>
      </header>

      <!--
        NOTES INTERNES — only rendered when the field is present.
        A non-participant won't receive notesInternesHtml at all (server omits it),
        so the whole section is gated on its presence.
      -->
      <section
        v-if="note.notesInternesHtml"
        class="chantier-section"
      >
        <h2 class="chantier-section__title">
          <feather-icon
            icon="LockIcon"
            size="18"
            class="mr-50"
          />
          Notes internes
        </h2>
        <div class="note-detail__internal-banner">
          <feather-icon
            icon="EyeOffIcon"
            size="13"
            class="mr-50"
          />
          Interne — visible uniquement par les participants au chantier.
        </div>
        <!-- eslint-disable-next-line vue/no-v-html -->
        <div
          class="note-detail__html"
          v-html="safeHtml(note.notesInternesHtml)"
        />
      </section>

      <!-- COMPTE-RENDU PUBLIC : indisponible sur un chantier privé -->
      <section
        v-if="isChantierPublic"
        class="chantier-section"
      >
        <h2 class="chantier-section__title">
          <feather-icon
            icon="GlobeIcon"
            size="18"
            class="mr-50"
          />
          Compte-rendu public
        </h2>
        <div
          v-if="note.compteRenduPublic"
          class="note-detail__public"
        >
          <div class="note-detail__public-banner">
            <feather-icon
              icon="GlobeIcon"
              size="13"
              class="mr-50"
            />
            Visible par tous les utilisateurs du CRM, y compris les utilisateurs externes.
          </div>
          <p class="note-detail__public-text">
            {{ note.compteRenduPublic }}
          </p>
        </div>
        <p
          v-else
          class="note-detail__empty"
        >
          Aucun compte-rendu public n'a été rédigé pour cette réunion.
        </p>
      </section>

      <!-- ACTIONS -->
      <div class="note-detail__actions">
        <b-button
          variant="outline-secondary"
          @click="onBack"
        >
          <feather-icon
            icon="ArrowLeftIcon"
            size="14"
            class="mr-50"
          />
          Retour
        </b-button>
        <b-button
          variant="outline-primary"
          @click="onPrint"
        >
          <feather-icon
            icon="PrinterIcon"
            size="14"
            class="mr-50"
          />
          Imprimer
        </b-button>
      </div>

    </div>

    <div
      v-else
      class="content-body"
    >
      <div class="chantier-warning">
        <feather-icon
          icon="AlertTriangleIcon"
          size="16"
          class="mr-50"
        />
        Note introuvable.
      </div>
    </div>
  </div>
</template>

<script>
import { BButton, BSpinner } from 'bootstrap-vue'
import { safeHtml } from './safe-html'

const MONTHS_LONG = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre']

export default {
  name: 'NoteDetailView',
  components: { BButton, BSpinner },
  data() {
    return {
      note: null,
      chantier: null,
      loading: true,
      error: null,
    }
  },
  computed: {
    chantierId() { return this.$route.params.chantierId },
    planId() { return this.$route.params.planId },
    noteId() { return this.$route.params.noteId },
    // Le compte-rendu public n'est exploité que sur un chantier public.
    isChantierPublic() { return !!(this.chantier && this.chantier.visibilite === 1) },
  },
  created() {
    this.loadNote()
  },
  methods: {
    safeHtml,
    async loadNote() {
      this.loading = true
      this.error = null
      try {
        const [note, chantier] = await Promise.all([
          this.$http.myressif.chantiers.getNote(this.chantierId, this.planId, this.noteId),
          this.$http.myressif.chantiers.getById(this.chantierId),
        ])
        this.note = note
        this.chantier = chantier
      } catch (e) {
        this.note = null
        if (e && e.response && e.response.status === 404) {
          // not-found falls through to the "Note introuvable" empty state
        } else {
          this.error = 'Une erreur est survenue lors du chargement de la note.'
        }
      } finally {
        this.loading = false
      }
    },
    formatLongDate(iso) {
      const d = new Date(iso)
      return `${d.getDate()} ${MONTHS_LONG[d.getMonth()]} ${d.getFullYear()}`
    },
    onBack() {
      this.$router.push({
        name: 'chantier-plan-detail',
        params: { chantierId: this.chantierId, planId: this.planId },
      })
    },
    onPrint() { window.print() },
  },
}
</script>

<style scoped lang="scss">
$at-green:   #1c4d4d;
$at-yellow:  #fcf5a6;
$at-orange:  #ff6131;
$at-beige:   #fffced;
$at-ink-900: #0F1F1F;
$at-ink-700: #2A3F3F;
$at-ink-500: #5A7373;
$at-ink-100: #E4ECEC;

.chantier-back {
  display: inline-flex;
  align-items: center;
  color: $at-ink-500;
  font-size: 13px;
  margin-bottom: 12px;
  text-decoration: none;
  &:hover { color: $at-orange; }
}

.chantier-state {
  text-align: center;
  padding: 48px 16px;
  color: $at-ink-500;
  background: #fff;
  border: 1px dashed $at-ink-100;
  border-radius: 10px;

  &--error {
    color: $at-orange;
    border-color: rgba(255, 97, 49, .3);
  }
}

.ataviti-home {
  &__banner {
    background: $at-yellow;
    border-radius: 12px;
    padding: 28px 32px;
    margin-bottom: 24px;
  }
  &__banner-eyebrow {
    color: $at-green;
    font-size: 13px;
    font-weight: 600;
    letter-spacing: .08em;
    text-transform: uppercase;
    margin: 0 0 8px;
  }
  &__title {
    font-family: 'Iowan Old Style', 'Palatino Linotype', Georgia, serif;
    font-size: 26px;
    color: $at-green;
    font-weight: 500;
    margin: 0 0 14px;
    letter-spacing: -.01em;
    line-height: 1.2;
  }
}

.note-detail {
  &__meta {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
  }
  &__date {
    display: inline-flex;
    align-items: center;
    color: $at-ink-700;
    font-size: 13px;
    font-weight: 500;
  }
  &__public-badge {
    background: rgba(47, 125, 107, .15);
    color: #2f7d6b;
    font-size: 11px;
    font-weight: 600;
    padding: 5px 10px;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
  }
  &__internal-badge {
    background: rgba(28, 77, 77, .12);
    color: $at-green;
    font-size: 11px;
    font-weight: 600;
    padding: 5px 10px;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
  }
  &__internal-banner {
    background: rgba(255, 97, 49, .08);
    color: $at-orange;
    border-left: 3px solid $at-orange;
    font-size: 12px;
    font-weight: 500;
    padding: 8px 12px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    margin-bottom: 16px;
  }
  &__html {
    color: $at-ink-900;
    font-size: 14px;
    line-height: 1.65;

    ::v-deep p { margin: 0 0 10px; }
    ::v-deep ul, ::v-deep ol { margin: 0 0 10px; padding-left: 22px; }
    ::v-deep li { margin-bottom: 4px; }
    ::v-deep strong { color: $at-green; }
    ::v-deep blockquote {
      border-left: 3px solid $at-ink-100;
      padding-left: 12px;
      color: $at-ink-700;
      font-style: italic;
      margin: 10px 0;
    }
  }
  &__public-banner {
    background: rgba(47, 125, 107, .08);
    color: #2f7d6b;
    border-left: 3px solid #2f7d6b;
    font-size: 12px;
    font-weight: 500;
    padding: 8px 12px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    margin-bottom: 14px;
  }
  &__public-text {
    color: $at-ink-900;
    font-size: 14px;
    line-height: 1.6;
    margin: 0;
  }
  &__empty {
    color: $at-ink-500;
    font-style: italic;
    font-size: 13px;
    margin: 0;
  }
  &__actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin-top: 12px;
  }
}

.chantier-section {
  background: #fff;
  border: 1px solid $at-ink-100;
  border-radius: 10px;
  padding: 22px 24px;
  margin-bottom: 18px;

  &__title {
    display: flex;
    align-items: center;
    font-size: 16px;
    color: $at-ink-900;
    font-weight: 600;
    margin: 0 0 14px;
  }
}

.chantier-warning {
  display: flex;
  align-items: center;
  background: rgba(240, 168, 48, .12);
  border: 1px solid rgba(240, 168, 48, .35);
  color: #8a5a0a;
  border-radius: 8px;
  padding: 10px 14px;
  margin-bottom: 18px;
  font-size: 13px;
}

@media print {
  ::v-deep .vertical-layout .main-menu,
  ::v-deep .vertical-layout .header-navbar,
  ::v-deep .vertical-layout .footer,
  ::v-deep .breadcrumb,
  .chantier-back,
  .note-detail__actions { display: none !important; }
}
</style>

<style lang="scss">
/* Global print rules — hide CRM chrome when printing a note */
@media print {
  .vertical-nav-menu,
  .header-navbar,
  .vertical-layout .main-menu,
  .vertical-layout .header-navbar,
  .vertical-layout .footer,
  .breadcrumb,
  .app-content > .content-overlay,
  .customizer { display: none !important; }

  .vertical-layout.vertical-menu-modern .app-content,
  .app-content {
    margin-left: 0 !important;
    padding: 12px !important;
  }
  body { background: white !important; }
}
</style>
